import React, {useState, useEffect} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  Button,
  ModalCloseButton,
  ModalBody,
  Stack,
  Box,
  Divider,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  Text,
  IconButton,
  ModalFooter,
  Center,
  Spinner,
  Input,
} from '@chakra-ui/react';
import Pagination from '../General/Pagination';
import {useQuery} from 'react-query';
import {get} from '../../service/ApiService';
import {formatDate} from '../../helpers/Functions';
import ServiceTypes from '../../constants/ServiceTypes';
import {Survey} from '../../service/Urls';
import {FaEdit} from 'react-icons/fa';
import {getServiceType} from '../../helpers/ServiceTypeDictionary';
import {useForm} from 'react-hook-form';
import ServiceTypeList from '../General/ServiceTypeList';
import {useNavigate} from 'react-router-dom';
import {debounce} from 'lodash';

const PAGE_SIZE = 8;
const DEBOUNCE_TIME = 600;

export default function SurveyTableModal({
  filterName,
  filterId,
  isOpen,
  onClose,
}: {
  filterName: string;
  filterId: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);

  // Search
  const [contractNumber, setContractNumber] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [recordNumber, setRecordNumber] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [buyerFirstName, setBuyerFirstName] = useState('');
  const [buyerLastName, setBuyerLastName] = useState('');
  const [deceasedFirstName, setDeceasedFirstName] = useState('');
  const [deceasedLastName, setDeceasedLastName] = useState('');
  const [arrangerName, setArrangerName] = useState('');

  const navigate = useNavigate();

  const {control, watch, setValue} = useForm();

  const serviceType = watch('ServiceType');

  const {data: surveys, isFetching} = useQuery(
    [
      'surveys',
      page,
      filterId,
      serviceType,
      contractNumber,
      batchNumber,
      recordNumber,
      contractDate,
      buyerFirstName,
      buyerLastName,
      deceasedFirstName,
      deceasedLastName,
      arrangerName,
    ],
    async () => {
      return get(
        `${Survey.LIST}?pageSize=${PAGE_SIZE}&page=${page}&${filterName}=${filterId}&serviceType=${serviceType}&contractNumber=${contractNumber}&batchNumber=${batchNumber}&recordNumber=${recordNumber}&contractDate=${contractDate}&buyerFirstName=${buyerFirstName}&buyerLastName=${buyerLastName}&deceasedFirstName=${deceasedFirstName}&deceasedLastName=${deceasedLastName}&arrangerName=${arrangerName}`
      );
    },
    {
      enabled: filterId != null && serviceType != null,
      keepPreviousData: true,
    }
  );

  const edit = (masterId: string) => {
    const svcType = getServiceType(+serviceType);
    navigate('/surveys/' + svcType.route, {state: {id: masterId, serviceType: svcType}});
  };

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setValue('ServiceType', '1');
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [serviceType]);

  return (
    <Modal size={'full'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginX="6rem" marginTop="3rem">
        <ModalHeader id="header">Surveys</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Center p={8}>
            <form>
              <ServiceTypeList control={control} defaultValue={1} />
            </form>
          </Center>
          <TableContainer position="relative" minH="200px">
            <Center
              position="absolute"
              left="50%"
              top="45%"
              bgColor="whiteAlpha.900"
              padding={3}
              borderRadius={8}
              opacity={isFetching ? 1 : 0}
              visibility={isFetching ? 'visible' : 'hidden'}
              transition="opacity 500ms ease-in"
              transitionDelay="150ms"
            >
              <Spinner speed="700ms" />
            </Center>
            <Table border="solid 2px #0c5f78" variant="striped" colorScheme="mint" size={'md'}>
              <Thead>
                <Tr border="solid 2px #0c5f78">
                  <Th />
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={contractNumber}
                      onChange={debounce(event => {
                        setPage(1);
                        setContractNumber(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={batchNumber}
                      onChange={debounce(event => {
                        setPage(1);
                        setBatchNumber(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={recordNumber}
                      onChange={debounce(event => {
                        setPage(1);
                        setRecordNumber(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      type="date"
                      defaultValue={contractDate}
                      onChange={event => {
                        setPage(1);
                        setContractDate(event.target.value);
                      }}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={buyerFirstName}
                      onChange={debounce(event => {
                        setPage(1);
                        setBuyerFirstName(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={buyerLastName}
                      onChange={debounce(event => {
                        setPage(1);
                        setBuyerLastName(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={deceasedFirstName}
                      onChange={debounce(event => {
                        setPage(1);
                        setDeceasedFirstName(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={deceasedLastName}
                      onChange={debounce(event => {
                        setPage(1);
                        setDeceasedLastName(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                  <Th>
                    <Input
                      borderColor="gray.300"
                      defaultValue={arrangerName}
                      onChange={debounce(event => {
                        setPage(1);
                        setArrangerName(event.target.value);
                      }, DEBOUNCE_TIME)}
                    />
                  </Th>
                </Tr>
                <Tr border="solid 2px #0c5f78">
                  <Th />
                  <Th>Contract Number</Th>
                  <Th>Batch Number</Th>
                  <Th>Record Number</Th>
                  <Th>Contract Date</Th>
                  <Th>Buyer First Name</Th>
                  <Th>Buyer Last Name</Th>
                  <Th>Deceased First Name</Th>
                  <Th>Deceased Last Name</Th>
                  <Th>Arranger Name</Th>
                </Tr>
              </Thead>
              <Tbody>
                {surveys?.data?.map((survey, index) => {
                  return (
                    <Tr key={survey.id}>
                      <Td>
                        <IconButton
                          colorScheme="tertiary"
                          title="Edit"
                          size="md"
                          icon={<FaEdit />}
                          mr="2"
                          onClick={() => edit(survey.id)}
                          aria-label="Edit"
                        />
                      </Td>
                      <Td>{survey.contractNumber}</Td>
                      <Td>{survey.batchNumber}</Td>
                      <Td>{survey.recordNumber}</Td>
                      <Td>{formatDate(survey.contractDate)}</Td>
                      <Td>{survey.buyerFirstName}</Td>
                      <Td>{survey.buyerLastName}</Td>
                      <Td>{survey.deceasedFirstName}</Td>
                      <Td>{survey.deceasedLastName}</Td>
                      <Td>{survey.arrangerName}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            <Pagination page={page - 1} pageCount={surveys?.pageCount ?? 0} onPageChange={({selected}) => setPage(selected + 1)} />
          </TableContainer>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
